import React, { FC, useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { Paper, Tab, Box, Grid, Grow, Stack, Tooltip } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';

import CompanyContacts, {
  UserRow,
} from 'organisms/CompanyDetailsCard/CompanyContacts';

import {
  AlertBox,
  CompanyDetailsText,
  CompanyInfo,
  CompanyInfoDetails,
  CompanyInfoLeftColumnStyled,
  StyledBody2,
} from 'pages/CompanyPage/CompanyPage.styles';
import { GreyTxt } from 'utils/styles/GreyText.styles';
import {
  StyledCompanyDetailsButton,
  StyledCurrencyButton,
} from 'organisms/CompanyDetailsCard/CompanyDetailsCard.styles';
import { StyledTabList } from 'molecules/Card/Card.styles';

import { formatDate } from 'utils/helpers/dates';
import { currencyFormatter } from 'utils/helpers/currencyFormatter';
import axios from 'axios';

import { companyType } from 'pages/CompanyPage/types';

import { ReactComponent as MapsHomeWorkOutlinedIcon } from 'assets/icons/adress.svg';
import { ReactComponent as MailOutlinedIcon } from 'assets/icons/email.svg';
import { ReactComponent as LanguageIcon } from 'assets/icons/domain.svg';
import { ReactComponent as GoogleIcon } from 'assets/icons/google.svg';
import { ReactComponent as BrokenLinkIcon } from 'assets/icons/broken-link.svg';
import { ReactComponent as ZohoCrmIcon } from 'assets/icons/zoho_crm.svg';
import { ReactComponent as ZohoBooksIcon } from 'assets/icons/zoho_books.svg';
// import { ReactComponent as EasyOpsIcon } from 'assets/icons/easyops.svg';
import { ReactComponent as QuickbooksIcon } from 'assets/icons/quickbooks.svg';
import { ReactComponent as MoneyIcon } from 'assets/icons/money.svg';
import { ReactComponent as CurrencyIcon } from 'assets/icons/currency.svg';
import { ReactComponent as VatIcon } from 'assets/icons/vat.svg';
import { CaptchaModal } from 'molecules/CaptchaModal/CaptchaModal';
import { useBaoSelector } from 'utils/hooks/redux';
import { SUPER_ADMIN } from 'utils/constants/roles';
import { palette } from 'utils/styles/variables';
import DeleteContactModal from 'organisms/Companies/components/DeleteContactModal';
import CurrencySetModal from 'organisms/Companies/components/CurrencySetModal';
import { shortenString } from 'utils/helpers/string';
import { neutrals } from 'utils/styles/color';
import CompanyDeals from 'organisms/CompanyDetailsCard/CompanyDeals';
import CompanyGooglers from './CompanyGooglers';
import { useTenant } from 'tenants/TenantProvider';
import CompanyReports from 'pages/CompanyPage/Reports';

const emptyState = { show: false, data: null };
export interface ICompanyDeal {
  hs_object_id: number;
  company_hs_id: number;
  sale_rep_name: string;
  close_date: string;
  status: string;
  pipeline: string;
  google_sales_rep_email: string;
  google_sales_rep_name: string;
  deal_stage: string;
}

export interface ICompanyGoogler {
  id: string;
  name: string;
  company_level_googler: boolean;
}

export const CompanyDetailsCard: FC<{
  company: companyType;
  paymentMethod: string;
  onChange: () => Promise<void>;
}> = ({ company, paymentMethod, onChange }) => {
  const { branding } = useTenant();
  const { hsId } = useParams() as { hsId: string };

  const { value: user } = useBaoSelector((state) => state.user);

  const isSuperAdmin = user.userType === SUPER_ADMIN;
  // eslint-disable-next-line
  // @ts-ignore
  // const isTestCompany = company.companyHsId === 26139845223;

  const [selectedTab, setSelectedTab] = useState<
    'details' | 'contacts' | 'deals' | 'googlers' | 'reports'
  >('details');
  const [itemCopied, setItemCopied] = useState<string>('');

  const [deals, setDeals] = useState<ICompanyDeal[]>([]);
  const [googlers, setGooglers] = useState<ICompanyGoogler[]>([]);
  const [users, setUsers] = useState([]);
  const [deleteUser, setDeleteUser] = useState<{
    show: boolean;
    data: UserRow | null;
  }>(emptyState);
  const [showDeleteCaptcha, setShowDeleteCaptcha] = useState<boolean>(false);
  // Controls currency dialog state
  const [currencyDialog, setCurrencyDialog] = useState<boolean>(false);

  const handleChangeTabs = useCallback(
    (
      event: React.SyntheticEvent,
      newValue: 'details' | 'contacts' | 'deals' | 'googlers'
    ) => {
      setSelectedTab(newValue);
    },
    []
  );

  const handleDeleteUser = useCallback((v: UserRow) => {
    setDeleteUser({ show: true, data: v });
  }, []);

  const handleResendInvite = useCallback(async (v: UserRow) => {
    try {
      await axios.get(
        `${process.env.REACT_APP_USER_SERVICE}/resend/invite?user_id=${v?.id}`
      );
      fetchPageData();
    } catch (err) {
      // eslint-disable-next-line
      console.log(err);
    }
  }, []);

  const onClose = () => {
    setDeleteUser((prev) => ({ ...prev, show: false }));
  };

  const handleReset = useCallback(() => {
    setDeleteUser(emptyState);
  }, []);

  const showDeleteCaptchaDialog = useCallback(() => {
    setShowDeleteCaptcha(true);
    onClose();
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    setShowDeleteCaptcha(false);
    handleReset();

    axios
      .delete(
        `${process.env.REACT_APP_USER_SERVICE}/delete/${deleteUser.data?.id}`
      )
      .then(fetchPageData);
  }, [deleteUser]);

  const handleUpdate = useCallback(async () => {
    await fetchPageData();
  }, [company.companyHsId]);

  useEffect(() => {
    (async () => {
      await fetchPageData();
    })();
  }, [company.companyHsId]);

  async function fetchPageData() {
    try {
      const { data: usersData } = await axios.get(
        `${process.env.REACT_APP_USER_SERVICE}/users`,
        {
          params: {
            page: 1,
            limit: 100,
            user_types: 'company_admin,company_viewer',
            company_hs_id: hsId,
            sort: 'created_at',
          },
        }
      );

      if (usersData.users) {
        setUsers(usersData.users);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_REPORT_SERVICE}/deal/company`,
        {
          params: {
            page: 1,
            per_page: 100,
            company_id: hsId,
            sort: 'created_at',
          },
        }
      );

      if (data.deals) {
        setDeals(data.deals);
      }

      if (data.google_sales_rep) {
        setGooglers(data.google_sales_rep);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    }
  }

  function renderAutopaymentType() {
    let result = '';

    if (company.auto_pay) {
      if (paymentMethod === 'sepa_debit') {
        result = 'SEPA';
      } else {
        result = paymentMethod === 'card' ? 'CC' : 'ACH';
      }
    }

    return result;
  }

  function copyToClipboard(value: string, prop: string) {
    navigator.clipboard.writeText(value);

    setItemCopied(prop);

    setTimeout(() => {
      setItemCopied('');
    }, 1500);
  }

  function mountFullAddress(companyAddress: companyType) {
    return [
      companyAddress.address,
      companyAddress.address2,
      companyAddress.city,
      companyAddress.state,
      companyAddress.country,
      companyAddress.postalCode,
    ].reduce((acc, item) => {
      if (item) return acc ? `${acc}, ${item}` : item;

      return acc;
    }, '');
  }

  function a11yProps(index: number) {
    return {
      id: `tab-${index}`,
      'aria-controls': `tabpanel-${index}`,
    };
  }

  const billingId = !company.gcpBillingId?.includes('NO GCP')
    ? company.gcpBillingId.replace('gcp_billing_id_', '')
    : '';

  return (
    <Paper sx={{ pt: '16px', minHeight: '346px' }}>
      <TabContext value={selectedTab}>
        <StyledTabList
          onChange={handleChangeTabs}
          aria-label="Company Details and Contacts tabs"
          variant="fullWidth"
        >
          <Tab
            label="Details"
            {...a11yProps(0)}
            className={selectedTab === 'details' ? '.active' : ''}
            value="details"
          />
          <Tab
            label="Contacts"
            {...a11yProps(1)}
            className={selectedTab === 'contacts' ? '.active' : ''}
            value="contacts"
          />
          {/* {isTestCompany ? (
            <Tab
              label="Reports"
              {...a11yProps(1)}
              className={selectedTab === 'reports' ? '.active' : ''}
              value="reports"
            />
          ) : null} */}
          {deals.length > 0 && (
            <Tab
              label="Deals"
              {...a11yProps(1)}
              className={selectedTab === 'deals' ? '.active' : ''}
              value="deals"
            />
          )}
          {googlers.length > 0 && (
            <Tab
              label="Googlers"
              {...a11yProps(1)}
              className={selectedTab === 'googlers' ? '.active' : ''}
              value="googlers"
            />
          )}
        </StyledTabList>
        <TabPanel sx={{ padding: '16px', mt: 0 }} value="details">
          <Grid
            container
            spacing={2}
            sx={{
              '&>.MuiGrid-root.MuiGrid-item': {
                marginTop: '16px',
                paddingTop: 0,
              },
            }}
          >
            <CompanyInfoLeftColumnStyled item xs={8}>
              <CompanyInfo>
                <MapsHomeWorkOutlinedIcon
                  onClick={() =>
                    copyToClipboard(mountFullAddress(company), 'address')
                  }
                />
                <Box>
                  <Grow
                    in={itemCopied === 'address'}
                    style={{
                      transformOrigin: '0 0 0',
                      position: 'absolute',
                    }}
                  >
                    <AlertBox>Address copied</AlertBox>
                  </Grow>
                  {itemCopied !== 'address' && (
                    <StyledBody2 variant="body2" maxWidth="365px">
                      {shortenString(mountFullAddress(company), 160, '...')}
                    </StyledBody2>
                  )}
                </Box>
              </CompanyInfo>
              <CompanyInfo>
                <MailOutlinedIcon
                  onClick={() => copyToClipboard(company.email, 'email')}
                />
                <Box>
                  <Grow
                    in={itemCopied === 'email'}
                    style={{
                      transformOrigin: '0 0 0',
                      position: 'absolute',
                      maxWidth: '95%',
                    }}
                  >
                    <AlertBox>Email copied</AlertBox>
                  </Grow>
                  {itemCopied !== 'email' && (
                    <StyledBody2 variant="body2">
                      {shortenString(company.email, 100, '...')}
                    </StyledBody2>
                  )}
                </Box>
              </CompanyInfo>
              <CompanyInfo>
                <LanguageIcon
                  onClick={() => copyToClipboard(company.domain, 'domain')}
                />
                <Box>
                  <Grow
                    in={itemCopied === 'domain'}
                    style={{
                      transformOrigin: '0 0 0',
                      position: 'absolute',
                      maxWidth: '95%',
                    }}
                  >
                    <AlertBox>Domain copied</AlertBox>
                  </Grow>
                  {itemCopied !== 'domain' && (
                    <StyledBody2 variant="body2">
                      {shortenString(company.domain, 100, '...')}
                    </StyledBody2>
                  )}
                </Box>
              </CompanyInfo>
              <CompanyInfo>
                <GoogleIcon
                  color={palette.primary.main}
                  onClick={() => copyToClipboard(billingId, 'gcpBillingId')}
                />
                <Box>
                  <Grow
                    in={itemCopied === 'gcpBillingId'}
                    style={{
                      transformOrigin: '0 0 0',
                      position: 'absolute',
                      width: '95%',
                    }}
                  >
                    <AlertBox>GCP account copied</AlertBox>
                  </Grow>
                  {itemCopied !== 'gcpBillingId' && (
                    <StyledBody2 variant="body2">
                      {shortenString(billingId, 40, '...')}
                    </StyledBody2>
                  )}
                </Box>
              </CompanyInfo>
              <CompanyInfo
                $color={
                  company?.currency ? palette.primary.main : palette.error[500]
                }
              >
                {company?.currency ? <MoneyIcon /> : <CurrencyIcon />}
                <Box>
                  {company?.currency ? (
                    <StyledBody2 variant="body2">
                      {company?.currency} |{' '}
                      <span style={{ color: neutrals[500] }}>
                        {company?.qbRegion}{' '}
                        {company?.qbRegion?.toLowerCase() === 'india'
                          ? 'Zoho Books'
                          : 'QuickBooks'}
                      </span>
                    </StyledBody2>
                  ) : (
                    <>
                      {company.companyHsId && isSuperAdmin ? (
                        <StyledCurrencyButton
                          variant="text"
                          onClick={() => setCurrencyDialog(true)}
                        >
                          Select company currency
                        </StyledCurrencyButton>
                      ) : null}
                    </>
                  )}
                </Box>
              </CompanyInfo>
              {company?.qbRegion === 'Europe' && company?.vat && (
                <CompanyInfo>
                  <VatIcon />
                  <StyledBody2 variant="body2">{company.vat || ''}</StyledBody2>
                </CompanyInfo>
              )}
            </CompanyInfoLeftColumnStyled>
            <Grid item xs={4}>
              <CompanyInfoDetails>
                <GreyTxt>Customer since:</GreyTxt>
                <CompanyDetailsText ml="auto">
                  {company.since ? formatDate(company.since, { time: '' }) : ''}
                </CompanyDetailsText>
              </CompanyInfoDetails>
              <CompanyInfoDetails>
                <GreyTxt>{branding.discountLabel}:</GreyTxt>
                <CompanyDetailsText ml="auto">
                  {company.discount}%
                </CompanyDetailsText>
              </CompanyInfoDetails>
              <CompanyInfoDetails>
                <GreyTxt>Payment terms:</GreyTxt>
                <CompanyDetailsText ml="auto">
                  {company.paymentTerms}
                </CompanyDetailsText>
              </CompanyInfoDetails>
              <CompanyInfoDetails>
                <GreyTxt>Auto payment:</GreyTxt>
                <CompanyDetailsText
                  ml="auto"
                  color={company.auto_pay ? '#047857' : '#B91C1C'}
                >
                  {company.auto_pay ? 'Enabled' : 'Disabled'}
                </CompanyDetailsText>
                {company.auto_pay && (
                  <CompanyDetailsText ml="5px">
                    {renderAutopaymentType()}
                  </CompanyDetailsText>
                )}
              </CompanyInfoDetails>
              <CompanyInfoDetails>
                <GreyTxt>Total billings:</GreyTxt>
                <CompanyDetailsText ml="auto">
                  {company.lifetime_billings}
                </CompanyDetailsText>
              </CompanyInfoDetails>
              <CompanyInfoDetails>
                <GreyTxt>Total paid:</GreyTxt>
                <CompanyDetailsText ml="auto">
                  {currencyFormatter(
                    Number(
                      company.total_billing_amount
                        ? company.total_billing_amount
                        : 0
                    ),
                    company.currency
                  )}
                </CompanyDetailsText>
              </CompanyInfoDetails>
              <CompanyInfoDetails>
                <GreyTxt>Total due:</GreyTxt>
                <CompanyDetailsText ml="auto">
                  {currencyFormatter(company.total_due, company.currency)}
                </CompanyDetailsText>
              </CompanyInfoDetails>
              <CompanyInfoDetails>
                <GreyTxt>Total past due:</GreyTxt>
                <CompanyDetailsText ml="auto">
                  {currencyFormatter(
                    Number(company.past_due ? company.past_due : 0),
                    company.currency
                  )}
                </CompanyDetailsText>
              </CompanyInfoDetails>
            </Grid>

            <Grid item>
              <Stack direction="row" sx={{ gap: '1rem', paddingTop: '1rem' }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{ gap: '0.5rem' }}
                >
                  {company.hsCompanyLink && (
                    <StyledCompanyDetailsButton
                      target="_blank"
                      href={company.hsCompanyLink}
                      variant="contained"
                      startIcon={
                        <ZohoCrmIcon width={20} height={20} color="#FFFFFF" />
                      }
                      sx={{
                        backgroundColor: palette.primary.main,
                        '&:hover': {
                          backgroundColor: 'rgb(77, 111, 163)',
                        },
                      }}
                    >
                      Zoho CRM
                    </StyledCompanyDetailsButton>
                  )}
                  {company.syncWithZconnect === false && (
                    <Tooltip title="Broken integration with Zoho CRM">
                      <Box display="flex" alignItems="center">
                        <BrokenLinkIcon
                          width={20}
                          height={20}
                          color={palette.error[500]}
                        />
                      </Box>
                    </Tooltip>
                  )}
                </Stack>
                {company.qbCompanyLink ? (
                  <StyledCompanyDetailsButton
                    target="_blank"
                    href={company.qbCompanyLink}
                    variant="contained"
                    startIcon={
                      company.qbRegion?.toLowerCase() === 'india' ? (
                        <ZohoBooksIcon width={20} height={20} color="#FFFFFF" />
                      ) : (
                        <QuickbooksIcon
                          width={20}
                          height={20}
                          color="#FFFFFF"
                        />
                      )
                    }
                    sx={{
                      backgroundColor: palette.primary.main,
                      '&:hover': {
                        backgroundColor: 'rgb(77, 111, 163)',
                      },
                      // backgroundColor: company.qbCompanyLink
                      //   ? palette.primary.main
                      //   : palette.error[200],
                      // '&:hover': {
                      //   backgroundColor: company.qbCompanyLink
                      //     ? 'rgb(77, 111, 163)'
                      //     : palette.error[200],
                      // },
                    }}
                  >
                    {company.qbRegion?.toLowerCase() === 'india'
                      ? 'ZohoBooks'
                      : 'QuickBooks'}
                  </StyledCompanyDetailsButton>
                ) : null}
                {/* {isTestCompany ? (
                  <StyledCompanyDetailsButton
                    variant="contained"
                    startIcon={<EasyOpsIcon width={20} height={20} />}
                    sx={{
                      backgroundColor: palette.primary.main,
                      '&:hover': {
                        backgroundColor: 'rgb(77, 111, 163)',
                      },
                    }}
                  >
                    EasyOps
                  </StyledCompanyDetailsButton>
                ) : null} */}
              </Stack>
            </Grid>
          </Grid>
        </TabPanel>
        <TabPanel
          sx={{
            padding: '24px 8px',
            '-ms-overflow-style': 'none' /* Internet Explorer 10+ */,
            'scrollbar-width': 'none' /* Firefox */,
            overflow: 'scroll',
            '&::-webkit-scrollbar': {
              display: 'none' /* Safari and Chrome */,
            },
          }}
          value="contacts"
        >
          <CompanyContacts
            company_hs_id={hsId}
            users={users}
            onResendInvitation={handleResendInvite}
            onDelete={handleDeleteUser}
            onUpdate={handleUpdate}
          />
        </TabPanel>
        <TabPanel
          sx={{
            padding: '24px 8px',
          }}
          value="deals"
        >
          <CompanyDeals deals={deals} />
        </TabPanel>
        <TabPanel
          sx={{
            padding: '24px 8px',
            '-ms-overflow-style': 'none' /* Internet Explorer 10+ */,
            'scrollbar-width': 'none' /* Firefox */,
            overflow: 'scroll',
            '&::-webkit-scrollbar': {
              display: 'none' /* Safari and Chrome */,
            },
          }}
          value="googlers"
        >
          <CompanyGooglers company_hs_id={hsId} users={googlers} />
        </TabPanel>
        <TabPanel
          sx={{
            padding: '24px 8px',
            '-ms-overflow-style': 'none' /* Internet Explorer 10+ */,
            'scrollbar-width': 'none' /* Firefox */,
            overflow: 'scroll',
            '&::-webkit-scrollbar': {
              display: 'none' /* Safari and Chrome */,
            },
          }}
          value="reports"
        >
          <CompanyReports />
        </TabPanel>
      </TabContext>

      <CurrencySetModal
        hsId={hsId}
        open={currencyDialog}
        onClose={() => setCurrencyDialog(false)}
        onSubmit={() => {
          setCurrencyDialog(false);
          onChange();
        }}
      />

      <DeleteContactModal
        open={deleteUser.show}
        user={deleteUser.data as UserRow}
        onSubmit={showDeleteCaptchaDialog}
        onClose={handleReset}
      />

      {showDeleteCaptcha && (
        <CaptchaModal
          onComplete={handleDeleteConfirm}
          onClose={() => setShowDeleteCaptcha(false)}
        />
      )}
    </Paper>
  );
};
